import { graphql, Link, useStaticQuery } from "gatsby";
import React, { FC, ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Index } from "elasticlunr";
import { Transition } from "react-transition-group";
import classNames from "classnames";
import { SearchInput, TextInputVariant } from "kaleidoscope/src";
import forceReflow from "kaleidoscope/src/utils/forceReflow";
import Text, { TextSize, TextTone } from "../../components/Text";
import getPortalRoot from "kaleidoscope/src/utils/getPortalRoot";
import { ReactComponent as Reference } from "../../assets/reference.svg";
import { ReactComponent as Docs } from "../../assets/docs.svg";

interface ISearchProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const Search: FC<ISearchProps> = ({ open, setOpen }) => {
  const [searchIndex, setSearchIndex] = useState<any>();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [resultFocusIndex, setResultFocusIndex] = useState(0);
  const overlayRef = useRef<HTMLInputElement>();
  const resultsRef = useRef<HTMLDivElement>();
  const portalRoot = useRef(getPortalRoot("search-root"));

  const data = useStaticQuery(graphql`
    query SearchIndexQuery {
      siteSearchIndex {
        index
      }
    }
  `);

  // Initialize search index
  useEffect(() => {
    setSearchIndex(Index.load(data.siteSearchIndex.index));
  }, [data]);

  // Listen for keyboard events
  useEffect(() => {
    const handleSearchToggle = (event: KeyboardEvent) => {
      const isMacOS = navigator.platform === "MacIntel";
      const isMacOSShortcut = isMacOS && event.key === "k" && event.metaKey;
      const isOtherOSShortcut = !isMacOS && event.key === "k" && event.ctrlKey;

      if (isMacOSShortcut || isOtherOSShortcut) {
        event.preventDefault();
        setOpen(!open);
      }

      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", handleSearchToggle);

    return () => {
      document.removeEventListener("keydown", handleSearchToggle);
    };
  }, [open]);

  // Manage focus
  useEffect(() => {
    let prevFocusElement: HTMLElement;

    if (open) {
      // This is a bit of a hack to focus the input. Consider forwarding refs to `SearchInput`
      const inputElement = overlayRef.current.querySelector("input");

      prevFocusElement = document.activeElement as HTMLElement;
      inputElement?.focus();
    }

    return () => {
      prevFocusElement?.focus();
    };
  }, [open]);

  const handleSearch = (value: string) => {
    const results = searchIndex
      .search(value, { expand: true })
      .map(({ ref }: { ref: string }) => searchIndex.documentStore.getDoc(ref));

    setQuery(value);
    setResults(results);
  };

  const handleSearchKeydown = (event: any) => {
    const { length } = results;
    const prevIndex = (resultFocusIndex - 1 + length) % length;
    const nextIndex = (resultFocusIndex + 1) % length;
    const resultItems = resultsRef.current?.childNodes;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setResultFocusIndex(nextIndex);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setResultFocusIndex(prevIndex);
    } else if (event.key === "Enter") {
      const currentItem = resultItems[resultFocusIndex] as HTMLElement;
      currentItem?.click();
    }
  };

  const clearSearch = () => {
    setQuery("");
    setResults([]);
    setResultFocusIndex(0);
  };

  const close = () => setOpen(false);

  const renderCategoryIcon = (category: string): ReactNode => {
    switch (category) {
      case "docs":
        return <Docs />;
      case "OpenApiIndex":
        return <Reference />;
      default:
        return null;
    }
  };

  return (
    <Transition mountOnEnter unmountOnExit in={open} timeout={400} onEnter={forceReflow} onExited={clearSearch}>
      {(status) =>
        createPortal(
          <div className={classNames("kld-search", `kld-search--${status}`)}>
            <div className={classNames("kld-search__backdrop", `kld-search__backdrop--${status}`)} onClick={close} />
            <div className="kld-search__overlay-frame">
              <div className={classNames("kld-search__overlay", `kld-search__overlay--${status}`)} ref={overlayRef}>
                <SearchInput
                  label="Search docs"
                  placeholder="Search docs"
                  value={query}
                  variant={TextInputVariant.Borderless}
                  onChange={handleSearch}
                  onClear={clearSearch}
                  onKeyDown={handleSearchKeydown}
                />
                <div className="kld-search__results" ref={resultsRef}>
                  {results?.map(({ slug, title, id, category }, index) => (
                    <Link
                      className={classNames("kld-search__result-link", {
                        "kld-search__result-link--active": resultFocusIndex === index,
                      })}
                      key={id}
                      to={slug}
                      onClick={close}
                    >
                      {renderCategoryIcon(category)}
                      <Text size={TextSize.Large}>{title}</Text>
                    </Link>
                  ))}
                  {query.length > 1 && !results.length && (
                    <Text tone={TextTone.Secondary} className="kld-search__result-empty">
                      No results found
                    </Text>
                  )}
                </div>
              </div>
            </div>
          </div>,
          portalRoot.current,
        )
      }
    </Transition>
  );
};

export default Search;
